/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */
import './tailwind.scss'
import packageJson from '@root/package.json'
import {
  NetworkTypes,
  Pages,
  TransactionTypes,
  Themes,
  NavDirections,
  NavMinimizationTogglerPositions,
  ScanTypes,
  SuiNetworks,
} from '@packages/utils/enums'
import { PassportEvents } from '@lib/events/events.enums'
import { initAnalytics } from '@lib/mixpanel/mixpanel'
import AbstractPassport from '@packages/utils/classes/passport.abstract'
import type {
  TPassportConfig,
  TPassportConstructor,
  TUser,
  TToastMessageOpts,
  TSuiNetwork,
  TPassortSuiExtension,
} from '@packages/utils/types'
import { accessTokenStore, userStore, isLoggedInStore, fn, suiFn } from '@packages/stores'
import {
  passportAuth,
  passportUi,
  passportInit,
  passportBlockchain,
  passportUser,
  passportEvents,
  passportAirdrop,
  passportCommon,
} from '@src/passport/index'
import { cloakStore } from '@src/stores/config'
import { getSuiCredCoinType } from './utils/blockchain'

export class Passport implements AbstractPassport {
  static version = packageJson.version
  static suiNetworks = SuiNetworks
  static pages = Pages
  static paymentTypes = TransactionTypes
  static themes = Themes
  static navDirections = NavDirections
  static navMinimizationTogglerPositions = NavMinimizationTogglerPositions
  static scanTypes = ScanTypes
  static events = PassportEvents

  public accessToken: string | null = null
  public config: TPassportConfig<TPassortSuiExtension>
  public isLoggedIn = false
  public user: TUser | null = null
  public suiNetwork: TSuiNetwork
  public suiPackage: string
  public networkType: NetworkTypes.MAINNET | NetworkTypes.TESTNET

  constructor(opts: TPassportConstructor<TPassortSuiExtension>) {
    this.destroy()
    if (opts?.suiNetwork && !(<TSuiNetwork[]>Object.values(SuiNetworks)).includes(opts?.suiNetwork)) {
      throw new Error('Unsupported Sui network. Check "Passport.suiNetworks" for available networks.')
    }
    this.configurePassport(opts)
    fn.set({
      // ui
      openUI: this.openUI,
      close: this.close,
      toastAlert: (message: string, type: 'success' | 'warning' | 'failure' | 'info', opts: TToastMessageOpts) => {
        if (this.networkType === NetworkTypes.MAINNET) return
        return this.toastAlert(message, type, opts)
      },
      // auth
      logout: this.logout,
      login: this.login.bind(this),
      // user
      updateProfile: this.updateProfile,
      confirmAccount: this.confirmAccount,
      deleteAccount: this.deleteAccount,
      // blockchain
      getCREDContract: getSuiCredCoinType,
      getBalance: this.getBalance,
      checkMembership: this.checkMembership,
      sendTokens: this.sendTokens,
      requestLoyaltyPoints: this.requestLoyaltyPoints,
    })

    suiFn.set({
      requestAirDropAsset: this.requestAirDropAsset,
      requestAirDropCoin: this.requestAirDropCoin,
    })

    userStore.subscribe((usr) => (this.user = usr))
    isLoggedInStore.subscribe((isLoggedIn) => {
      this.isLoggedIn = isLoggedIn || false
    })
    accessTokenStore.subscribe((token) => (this.accessToken = token))
    cloakStore.subscribe((enabled) => {
      if (enabled && typeof window !== 'undefined') void this.openUI()
    })
    
    initAnalytics(this.networkType)
  }

  login = passportAuth.login
  setAccessToken = passportAuth.setAccessToken
  logout = passportAuth.logout

  openUI = passportUi.openUI
  close = passportUi.close
  toastAlert = passportUi.toastAlert
  destroy = passportUi.destroy
  hideNavigation = passportUi.hideNavigation
  showNavigation = passportUi.showNavigation

  configurePassport = passportInit.configurePassport
  init = passportInit.init

  checkMembership = passportBlockchain.checkMembership
  sendTokens = passportBlockchain.sendTokens
  getAddress = passportBlockchain.getAddress
  sendNft = passportBlockchain.sendNft
  switchChain = passportBlockchain.switchChain
  getBalance = passportBlockchain.getBalance

  getSuiClient = passportBlockchain.getSuiClient
  getUserCollectionAssetsWithBalances = passportBlockchain.getUserCollectionAssetsWithBalances
  requestAirDropCoin = passportAirdrop.requestAirDropCoin
  requestAirDropAsset = passportAirdrop.requestAirDropAsset

  updateProfile = passportUser.updateProfile
  confirmAccount = passportUser.confirmAccount
  deleteAccount = passportUser.deleteAccount
  getUser = passportUser.getUser
  friends = passportUser.friends

  requestLoyaltyPoints = passportCommon.requestLoyaltyPoints

  on = passportEvents.on
  once = passportEvents.once
}

export interface Passport {}
